import { X } from "lucide-react";
import { AGENTS, AGENT_BY_ID, ORACLE } from "../agents";

function Shell({ title, onClose, children, testid, wide }) {
  return (
    <div className="cine-sheet-wrap" onClick={onClose} data-testid={`${testid}-backdrop`}>
      <aside className={`cine-sheet ${wide ? "is-wide" : ""}`} role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="cine-display text-base md:text-lg font-bold">{title}</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="cine-btn" data-testid={`${testid}-close`}><X size={16} /></button>
        </div>
        {children}
      </aside>
    </div>
  );
}

export function AgentCard({ id, sc, onClose }) {
  if (id === "candidate") {
    return (
      <Shell title={`$${sc.token.sym} · synthetic token`} onClose={onClose} testid="cinema-agent-card">
        <p className="text-sm text-white/70">An invented example token used to show the flow. It does not exist on any chain, and its numbers are illustrative only.</p>
      </Shell>
    );
  }
  const a = AGENT_BY_ID[id];
  return (
    <Shell title={a.name} onClose={onClose} testid="cinema-agent-card">
      {id === "oracle" && <video src="/cinema/oracle.mp4" poster="/lore/oracle.jpg" autoPlay muted loop playsInline className="w-40 rounded-xl mb-4 bg-black" data-testid="oracle-art" />}
      <div className="flex items-center gap-2 mb-3"><span className="h-3 w-3 rounded-full" style={{ background: a.color, boxShadow: `0 0 14px ${a.color}` }} />
        <span className="cine-mono text-[11px] text-white/50">{a.shape ? `${a.shape} · ${a.motion}` : "Central coordinator"}</span></div>
      <p className="text-sm text-white/85">{a.task}</p>
      <div className="mt-5 cine-mono text-[10px] tracking-[0.25em] text-white/40">GROUPS THESE REAL RIPP COMPONENTS</div>
      <ul className="mt-2 space-y-1.5 text-[13px] text-white/70 list-disc pl-5" data-testid="agent-card-backend">{a.backend.map((b) => <li key={b}>{b}</li>)}</ul>
      <p className="mt-4 text-[11px] text-white/35">The character, colour and movement are a visual mapping created for this showcase.</p>
    </Shell>
  );
}

export function AgentRoster({ onSelect, selected }) {
  return (
    <div className="cine-roster" data-testid="cinema-agent-roster">
      {[ORACLE, ...AGENTS].map((a) => (
        <button key={a.id} type="button" onClick={() => onSelect(a.id)} className={`cine-chip ${selected === a.id ? "is-on" : ""}`} style={{ "--c": a.color }} data-testid={`roster-${a.id}`}>
          <span className="cine-chip-dot" />{a.name}
        </button>
      ))}
    </div>
  );
}

export function Journal({ entries, onClose }) {
  return (
    <Shell title="Decision journal (simulated)" onClose={onClose} testid="cinema-journal">
      {!entries.length && <p className="text-sm text-white/50" data-testid="journal-empty">No outcomes recorded yet. Entries appear when a scenario finishes.</p>}
      <ol className="space-y-2" data-testid="journal-list">
        {entries.map((e, i) => (
          <li key={i} className="rounded-xl border border-white/10 bg-white/[0.03] p-3 text-[13px]" data-testid={`journal-entry-${i}`}>
            <div className="flex justify-between"><span className="font-bold">${e.sym}</span><span className={e.kind === "gain" ? "text-emerald-300" : e.kind === "loss" ? "text-rose-300" : "text-white/60"}>{e.pct}</span></div>
            <div className="text-white/55 mt-1">{e.scenario} · {e.text}</div>
          </li>
        ))}
      </ol>
      <p className="mt-4 text-[11px] text-white/35">These are example outcomes from the simulation, not RIPP trading results.</p>
    </Shell>
  );
}

export function About({ onClose }) {
  return (
    <Shell title="What is simulated and what is real" onClose={onClose} testid="cinema-about" wide>
      <div className="grid md:grid-cols-2 gap-5 text-[13px] text-white/75">
        <div>
          <div className="cine-mono text-[10px] tracking-[0.25em] text-amber-300/80 mb-2">SIMULATED</div>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Every token ($NEONMOTH, $ROCKETEEL, $GLASSWASP, $VELVETBUG and the stream names) is invented.</li>
            <li>All prices, liquidity, wallet numbers, orders, fills and returns are illustrative.</li>
            <li>No live data feeds this page, and no orders are placed from it.</li>
            <li>The 3D engine, agent shapes and scale of activity are an artistic representation.</li>
          </ul>
        </div>
        <div>
          <div className="cine-mono text-[10px] tracking-[0.25em] text-emerald-300/80 mb-2">BASED ON THE REAL SYSTEM</div>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>The stage order matches the RIPP pipeline: discovery, Hive analysis, safety gates, execution, position management, exit and the decision log.</li>
            <li>Each agent card lists the real backend components that role represents.</li>
            <li>Rules shown (e.g. rug filter on pool size, pool-to-cap ratio and coin age, 75% sold at 2x with a moonbag riding, protective stops) mirror the engine's configured behaviour.</li>
          </ul>
        </div>
      </div>
      <div className="mt-5 text-[11px] text-white/40">The animated Oracle (from the existing RIPP Oracle artwork) and the fallback data-stream footage were generated with Higgsfield (Kling 3.0 Pro, Marketing Studio). The engine itself is real-time 3D. Labels and decision states are rendered live in code.</div>
    </Shell>
  );
}
